import { BadgeCheck, ShieldCheck } from "lucide-react";

export function SectionHeading({ kicker, title, sub, align = "center", testid }) {
  const alignCls = align === "center" ? "text-center mx-auto" : "text-left";
  return (
    <div className={`max-w-3xl ${alignCls} mb-12 md:mb-16`} data-testid={testid}>
      {kicker && <p className="gold-kicker mb-4">{kicker}</p>}
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100 whitespace-pre-line">{title}</h2>
      {sub && <p className="mt-4 text-base leading-relaxed text-slate-400">{sub}</p>}
    </div>
  );
}

export function MatchRing({ value = 96, size = 92, testid }) {
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid={testid}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1E2A3D" strokeWidth="5" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#D4AF37" strokeWidth="5"
          strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-lg font-semibold text-gold">{value}%</span>
        <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Match</span>
      </div>
    </div>
  );
}

const LEVEL_STYLES = {
  "REGISTERED": "border-slate-600 text-slate-400",
  "BUSINESS VERIFIED": "border-techblue text-techblue-light",
  "PROFILE VERIFIED": "border-techblue text-techblue-light",
  "TRUSTED BUSINESS": "border-gold text-gold",
};

export function VerifiedBadge({ level, testid }) {
  const Icon = level === "TRUSTED BUSINESS" ? ShieldCheck : BadgeCheck;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 border rounded-sm px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ${LEVEL_STYLES[level] || LEVEL_STYLES.REGISTERED}`}>
      <Icon size={12} /> {level}
    </span>
  );
}

export function StatCard({ label, value, delta, testid }) {
  return (
    <div className="card-surface p-5 md:p-6" data-testid={testid}>
      <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-2 font-mono text-2xl md:text-3xl font-semibold text-slate-100">{value}</p>
      {delta && <p className="mt-1 text-xs font-mono text-emerald-400">{delta}</p>}
    </div>
  );
}

export function FitBar({ label, value }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5">
        <span className="text-slate-400">{label}</span>
        <span className="font-mono text-slate-300">{value}%</span>
      </div>
      <div className="h-1 bg-navy-700 rounded-full overflow-hidden">
        <div className="h-full bg-gold rounded-full" style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

export function QRPattern({ size = 84, testid }) {
  const cells = 11;
  const seed = [1,0,1,1,0,1,0,0,1,1,0, 0,1,0,0,1,1,0,1,0,0,1, 1,1,0,1,0,0,1,0,1,0,1, 0,0,1,0,1,1,0,1,0,1,0, 1,0,0,1,0,0,1,1,0,0,1, 0,1,1,0,1,0,0,1,1,0,0, 1,0,0,1,0,1,1,0,0,1,1, 0,0,1,0,1,0,0,1,0,1,0, 1,1,0,0,1,1,0,0,1,0,1, 0,1,0,1,0,0,1,1,0,1,0, 1,0,1,0,1,0,1,0,1,1,0];
  const cell = size / cells;
  return (
    <svg width={size} height={size} className="bg-white rounded-sm p-1" viewBox={`0 0 ${size} ${size}`} data-testid={testid}>
      {seed.map((v, i) => v ? (
        <rect key={i} x={(i % cells) * cell} y={Math.floor(i / cells) * cell} width={cell} height={cell} fill="#0F172A" />
      ) : null)}
    </svg>
  );
}
